/**
 * Credits 系统类型定义
 */

// ==================== 基础枚举 ====================

/** 套餐类型 */
export enum PlanType {
    FREE = 'free',
    PRO = 'pro',
    ENTERPRISE = 'enterprise'
}

/** 商业档位 */
export enum TierLevel {
    FREE = 'free',
    PRO_100 = 'pro_100',
    PRO_200 = 'pro_200',
    PRO_400 = 'pro_400',
    PRO_600 = 'pro_600'
}

/** 租户类型 */
export enum TenantType {
    PERSONAL = 'personal',
    ENTERPRISE = 'enterprise'
}

/** Grant 来源 */
export enum GrantSource {
    FREE_30D_ACTIVITY = 'plg_30d_activity_grant',
    PAID_INITIAL = 'paid_subscription_initial',
    PAID_RENEWAL = 'paid_subscription_renewal',
    PAID_UPGRADE = 'paid_subscription_upgrade',
    MANUAL_BONUS = 'manual_bonus',
    MIGRATION = 'migration_grant',
    SERVICE_REFUND = 'service_refund_credit',
    PAYMENT_REVERSAL = 'payment_refund_reversal'
}

/** Grant 类型 */
export enum GrantType {
    FREE = 'free',
    PAID = 'paid',
    PROMOTIONAL = 'promotional',
    ENTERPRISE = 'enterprise',
    RECHARGE = 'recharge'
}

/** Grant 状态 */
export enum GrantStatus {
    ACTIVE = 'active',
    CONSUMED = 'consumed',
    EXPIRED = 'expired',
    REVOKED = 'revoked',
    PENDING = 'pending'
}

/** 结算模式 */
export enum SettlementMode {
    /** Credits 用户：检查余额、计量、扣费 */
    CREDIT_CHARGE = 'credit_charge',
    /** 老计次用户：只计量，不查余额，不扣 Credits */
    COUNT_COVERED = 'count_covered',
    /** 内部测试：只计量，不收费 */
    METER_ONLY = 'meter_only',
    /** 免费豁免 */
    NO_CHARGE = 'no_charge'
}

/** 计费类型 */
export enum BillingType {
    COUNT = 'count',
    CREDITS = 'credits'
}

/** 套餐类型（新老） */
export enum PackageType {
    LEGACY_COUNT = 'legacy_count',
    CREDITS = 'credits'
}

/** 企业额度分配类型 */
export enum AllocationType {
    PERSONAL_DAILY_CAP = 'personal_daily_cap',
    ASSIGNED_POOL = 'assigned_pool',
    SHARED_POOL = 'shared_pool'
}

/** 组织 Credits 状态 */
export enum OrgCreditStatus {
    ACTIVE = 'active',
    INSUFFICIENT = 'insufficient',
    EXPIRED = 'expired',
    RESTRICTED = 'restricted'
}

/** 订阅状态 */
export enum SubscriptionStatus {
    ACTIVE = 'active',
    PAST_DUE = 'past_due',
    PAYMENT_FAILED = 'payment_failed',
    CANCELLED = 'cancelled',
    PENDING = 'pending'
}

/** Usage Record 状态 */
export enum UsageRecordStatus {
    COMPLETED = 'completed',
    FAILED = 'failed',
    PARTIALLY_COMPLETED = 'partially_completed',
    REFUNDED = 'refunded',
    PARTIALLY_REFUNDED = 'partially_refunded'
}

/** Credit Change 类型 */
export enum CreditChangeType {
    FREE_GRANT = 'free_grant',
    FREE_CLAIM = 'free_claim',
    PAID_GRANT = 'paid_grant',
    RENEWAL_GRANT = 'renewal_grant',
    RECHARGE = 'recharge',
    TRIAL_GRANT = 'trial_grant',
    MANUAL_ADJUSTMENT = 'manual_adjustment',
    EXPIRATION = 'expiration',
    STANDALONE_REFUND = 'standalone_refund'
}

/** 不可用原因 */
export enum UnavailableReason {
    INSUFFICIENT = 'insufficient',
    EXPIRED = 'expired',
    RESTRICTED = 'restricted',
    POLICY_BLOCKED = 'policy_blocked',
    NO_ACTIVE = 'no_active',
    DAILY_LIMIT_REACHED = 'daily_limit_reached',
    ASSIGNED_POOL_EXHAUSTED = 'assigned_pool_exhausted',
    ORG_INSUFFICIENT = 'organization_insufficient'
}

// ==================== 接口定义 ====================

/** 单个 Grant 信息 */
export interface CreditGrant {
    grantId: string;
    grantType: GrantType;
    source: GrantSource;
    totalCredits: number;
    availableCredits: number;
    usedCredits: number;
    expiredCredits: number;
    grantedAt: string;
    validFrom: string;
    expiresAt: string;
    status: GrantStatus;
    walletSource?: string;
}

/** 免费 Credits 信息 */
export interface FreeCreditsInfo {
    available: number;
    nearestExpiration?: {
        amount: number;
        expiresAt: string;
    };
    nextClaimDate?: string;
}

/** 付费 Credits 信息 */
export interface PaidCreditsInfo {
    available: number;
    grants: Array<{
        grantId: string;
        remaining: number;
        expiresAt: string;
    }>;
}

/** 订阅信息 */
export interface SubscriptionInfo {
    currentTier: TierLevel;
    currentPlan: PlanType;
    status: SubscriptionStatus;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    renewalDate: string;
    scheduledDowngrade?: {
        tier: TierLevel;
        effectiveAt: string;
    };
    stripePriceId?: string;
}

/** Credits Summary */
export interface CreditsSummary {
    /** 总可用余额 */
    totalAvailable: number;
    /** 免费 Credits */
    freeCredits: FreeCreditsInfo;
    /** 付费 Credits */
    paidCredits: PaidCreditsInfo;
    /** 订阅信息 */
    subscription?: SubscriptionInfo;
    /** 用户类型 */
    tenantType: TenantType;
    /** 套餐类型 */
    planType?: PlanType;
    /** 是否可升级 */
    canUpgrade: boolean;
    /** 是否可管理账单 */
    canManageBilling: boolean;
    /** 是否有老计次权益 */
    hasLegacyEntitlement?: boolean;
    /** 企业用户状态 */
    enterpriseStatus?: EnterpriseCreditsStatus;
}

/** 企业用户 Credits 状态 */
export interface EnterpriseCreditsStatus {
    allocationType: AllocationType;
    /** 组织名称 */
    orgName?: string;
    /** 个人日限额 */
    dailyCap?: {
        used: number;
        limit: number;
        timezone: string;
    };
    /** 个人分配子池 */
    assignedPool?: {
        used: number;
        limit: number;
        remaining: number;
        validUntil: string;
    };
    /** 今日使用量 */
    todayUsage: number;
    /** 组织状态 */
    orgStatus: OrgCreditStatus;
    restrictionCode?: string;
}

/** 钱包状态 */
export interface WalletStatus {
    available: number;
    isActive: boolean;
    unavailableReason?: UnavailableReason;
    grants: CreditGrant[];
    /** 企业用户额度状态 */
    enterpriseStatus?: EnterpriseCreditsStatus;
}

/** 套餐权益 */
export interface PackageEntitlement {
    packageId: string;
    packageType: PackageType;
    packageName: string;
    startAt: string;
    endAt: string;
    isActive: boolean;
    coveredFeatures: string[];
    billingType: BillingType;
    /** 老计次剩余次数 */
    remainingCount?: number;
    totalCount?: number;
}

/** 用户权益上下文 */
export interface EntitlementContext {
    userId: string;
    tenantId: string;
    tenantType: TenantType;
    planType: PlanType;
    packages: PackageEntitlement[];
    creditsActive: boolean;
    canUpgrade: boolean;
    canManageBilling: boolean;
    canContactAdmin: boolean;
}

/** Usage Record */
export interface UsageRecord {
    transactionId: string;
    sessionId: string;
    jobId?: string;
    featureId: string;
    displayName: string;
    creditChange: number;
    status: UsageRecordStatus;
    settlementTime: string;
    settlementMode: SettlementMode;
    /** 关联的 History session ID，用于跳转 */
    historySessionId?: string;
    /** Agent 类型，用于显示图标 */
    agentType?: string;
}

/** Credit Change Record */
export interface CreditChangeRecord {
    changeId: string;
    changeType: CreditChangeType;
    grantId?: string;
    grantType?: GrantType;
    creditChange: number;
    balanceAfter?: number;
    effectiveAt: string;
    expiresAt?: string;
    reasonCode?: string;
    displayName: string;
}

/** 升级确认信息 */
export interface UpgradeConfirmInfo {
    currentPlan: PlanType;
    currentTier: TierLevel;
    targetTier: TierLevel;
    paymentAmount: number;
    currency: string;
    newPeriodStart: string;
    newPeriodEnd: string;
    creditsToReceive: number;
    existingCreditsRetained: boolean;
    nextRenewalAmount: number;
    nextRenewalDate: string;
}

/** 降级确认信息 */
export interface DowngradeConfirmInfo {
    currentPlan: PlanType;
    currentTier: TierLevel;
    targetTier: TierLevel;
    effectiveDate: string;
    currentPeriodEnd: string;
    currentCreditsRetained: boolean;
    nextPeriodCredits: number;
    nextRenewalAmount: number;
}

/** 结算上下文 */
export interface SettlementContext {
    hasLegacyCount: boolean;
    legacyCountRemaining: number;
    hasCredits: boolean;
    creditsAvailable: number;
    featureId: string;
}

/** 结算结果 */
export interface SettlementResult {
    mode: SettlementMode;
    canProceed: boolean;
    reason?: UnavailableReason;
    estimatedCost?: number;
}

// ==================== API 请求/响应类型 ====================

/** 获取 Credits Summary 响应 */
export interface GetCreditsSummaryResponse {
    summary: CreditsSummary;
}

/** 获取 Usage Records 请求 */
export interface GetUsageRecordsParams {
    page?: number;
    pageSize?: number;
    startDate?: string;
    endDate?: string;
}

/** 获取 Usage Records 响应 */
export interface GetUsageRecordsResponse {
    records: UsageRecord[];
    total: number;
    page: number;
    pageSize: number;
}

/** 获取 Credit Changes 请求 */
export interface GetCreditChangesParams {
    page?: number;
    pageSize?: number;
    startDate?: string;
    endDate?: string;
}

/** 获取 Credit Changes 响应 */
export interface GetCreditChangesResponse {
    records: CreditChangeRecord[];
    total: number;
    page: number;
    pageSize: number;
}

/** 获取权益上下文响应 */
export interface GetEntitlementContextResponse {
    context: EntitlementContext;
}

/** 获取钱包状态响应 */
export interface GetWalletStatusResponse {
    wallet: WalletStatus;
}

/** 获取升级确认信息请求 */
export interface GetUpgradeConfirmParams {
    targetTier: TierLevel;
}

/** 获取升级确认信息响应 */
export interface GetUpgradeConfirmResponse {
    confirmInfo: UpgradeConfirmInfo;
}

/** 执行升级请求 */
export interface ExecuteUpgradeParams {
    targetTier: TierLevel;
}

/** 执行升级响应 */
export interface ExecuteUpgradeResponse {
    success: boolean;
    checkoutUrl?: string;
    error?: string;
}

/** 获取降级确认信息请求 */
export interface GetDowngradeConfirmParams {
    targetTier: TierLevel;
}

/** 获取降级确认信息响应 */
export interface GetDowngradeConfirmResponse {
    confirmInfo: DowngradeConfirmInfo;
}

/** 执行降级请求 */
export interface ExecuteDowngradeParams {
    targetTier: TierLevel;
}

/** 执行降级响应 */
export interface ExecuteDowngradeResponse {
    success: boolean;
    scheduledEffectiveAt?: string;
    error?: string;
}

/** 取消降级预约响应 */
export interface CancelDowngradeResponse {
    success: boolean;
    error?: string;
}

// ==================== 充值/消费记录相关类型 ====================

/** 充值记录项 */
export interface RechargeRecordItem {
    /** 租户用户ID (个人) */
    tenantUserId?: string;
    /** 公司ID (企业) */
    companyId?: string;
    /** 充值积分数 */
    credits: number;
    /** 来源 */
    source: string;
    /** 来源ID */
    sourceId?: string;
    /** 合同编号 (企业) */
    contractNo?: string;
    /** 生效开始时间 (时间戳) */
    beginAt: number;
    /** 生效结束时间 (时间戳) */
    endAt: number;
    /** 备注 */
    remark?: string;
    /** 充值时间 (时间戳) */
    rechargeAt: number;
}

/** 充值记录响应 */
export interface RechargeRecordsResponse {
    /** 总积分 */
    totalCredits: number;
    /** 已使用积分 */
    usedCredits: number;
    /** 充值记录列表 */
    items: RechargeRecordItem[];
}

/** 消费记录项 */
export interface ConsumeRecordItem {
    /** 记录ID */
    id: string;
    /** 业务ID */
    businessId: string;
    /** 业务标题 */
    businessTitle: string;
    /** 业务描述 */
    source: string;
    /** 业务原始类型 */
    rawType: string;
    /** 业务来源ID */
    sourceId: string;
    /** 描述 */
    description: string;
    /** 备注 */
    remark: string;
    /** 租户用户ID */
    tenantUserId: string;
    /** 公司ID */
    companyId?: string;
    /** 平台 */
    platform: string;
    /** 产品ID */
    productId: string;
    /** 扣费类型 */
    deductType: string;
    /** 功能ID */
    featureId: string;
    /** 功能名称 (中文) */
    featureNameCn: string;
    /** 功能名称 (英文) */
    featureNameEn: string;
    /** 功能名称 (日文) */
    featureNameJp: string;
    /** 功能名称 (繁体中文) */
    featureNameTw: string;
    /** 任务ID */
    jobId: string;
    /** 扣除积分数 */
    deductCredits: number;
    /** 数量 */
    quantity: number;
    /** 单位 */
    unit: string;
    /** 状态 */
    status: string;
    /** 积分类型 */
    creditsType: string;
    /** 单价 */
    unitPrice: number;
    /** 消费时间 (时间戳) */
    consumeAt: number;
    /** 创建时间 (时间戳) */
    createdAt: number;
    /** 扣费前余额 */
    balanceBeforeDeduct: number;
    /** MCP服务器ID */
    mcpServerId?: string;
    /** MCP工具ID */
    mcpToolId?: string;
    /** LLM模型 */
    llmModel?: string;
}

/** 消费记录查询参数 */
export interface ConsumeRecordsParams {
    /** 页码 */
    page_no?: number;
    /** 消费开始时间 */
    consumeAtStart?: number;
    /** 消费结束时间 */
    consumeAtEnd?: number;
    /** 页大小 */
    pageSize?: number;
    /** 游标ID */
    cursorId?: string;
    /** 游标创建时间 */
    cursorCreatedAt?: number | null;
    /** 状态 */
    status?: string;
}

/** 消费记录响应 */
export interface ConsumeRecordsResponse {
    /** 消费记录列表 */
    items: ConsumeRecordItem[];
    /** 总数 */
    totalSize: number;
    /** 页大小 */
    pageSize: number;
    /** 下一页游标创建时间 */
    nextCursorCreatedAt?: number;
    /** 下一页游标ID */
    nextCursorId?: string;
}

// ==================== 积分详情相关类型 ====================

/** 个人充值记录项 */
export interface PersonalRechargeItem {
    /** 租户用户ID */
    tenant_user_id: string;
    /** 充值积分数 */
    credits: number;
    /** 充值来源 */
    source: string;
    /** 来源业务ID */
    source_id: string;
    /** 充值模式 */
    charge_mode: string;
    /** 有效期开始时间 (毫秒时间戳) */
    begin_at: number;
    /** 有效期结束时间 (毫秒时间戳) */
    end_at: number;
    /** 备注 */
    remark: string;
    /** 充值时间 (毫秒时间戳) */
    recharge_at: number;
    /** 该充值记录已消耗积分 */
    used_credits?: number;
}

/** 个人积分详情响应 */
export interface PersonalCreditsDetailResponse {
    /** 用户个人总积分 */
    total_credits: number;
    /** 用户个人已使用积分 */
    used_credits: number;
    /** 用户当天已使用积分 */
    daily_used_credits: number;
    /** 冻结积分（不可用积分） */
    frozen_credits: number;
    /** 用户个人充值明细列表 */
    items: PersonalRechargeItem[];
}

/** 公司充值记录项 */
export interface CompanyRechargeItem {
    /** 公司ID */
    company_id: string;
    /** 充值积分数 */
    credits: number;
    /** 充值来源 */
    source: string;
    /** 充值模式 */
    charge_mode: string;
    /** 有效期开始时间 (毫秒时间戳) */
    begin_at: number;
    /** 有效期结束时间 (毫秒时间戳) */
    end_at: number;
    /** 合同号 */
    contract_no: string;
    /** 备注 */
    remark: string;
    /** 充值时间 (毫秒时间戳) */
    recharge_at: number;
    /** 创建人 */
    created_by: string;
    /** 创建时间 (毫秒时间戳) */
    created_at: number;
}

/** 公司积分详情响应 */
export interface CompanyCreditsDetailResponse {
    /** 公司总积分 */
    total_credits: number;
    /** 公司已使用积分 */
    used_credits: number;
    /** 当前用户当天使用的公司积分 */
    daily_used_credits: number;
    /** 冻结积分（不可用积分） */
    frozen_credits: number;
    /** 用户个人每日积分限额 */
    user_daily_limit: number;
    /** 公司维度下单用户每日积分限额 */
    company_user_daily_limit: number;
    /** 公司每日总积分限额 */
    company_total_daily_limit: number;
    /** 公司充值明细列表 */
    items: CompanyRechargeItem[];
}

/** 领取免费 Credits 响应 */
export interface ClaimFreeCreditsResponse {
    success: boolean;
    creditsGranted?: number;
    expiresAt?: string;
    nextClaimDate?: string;
    error?: string;
}

/** 积分充值资格信息 */
export interface RechargeEligibilityInfo {
    /** has_pay_packages为true表示用户有付费套餐，则不要自动充值积分 */
    has_pay_packages?: boolean;
    /** 当前账号是否有过个人积分充值记录 */
    has_recharge_records: boolean;
    /** 是否个人账号 */
    is_personal_account: boolean;
    /** 最新一次个人积分充值距今天然日是否大于 30 天 */
    is_latest_recharge_over_30_days: boolean;
}

/** 积分充值资格响应 */
export interface GetRechargeEligibilityResponse {
    status: boolean;
    error_code: number;
    data: RechargeEligibilityInfo;
}

/** 活动积分充值请求参数 */
export interface ActivityRechargeParams {
    /** 活动码 */
    activity_code: string;
    /** 平台标识 */
    platform?: string;
    /** 充值来源 */
    source?: string;
}

/** 活动积分充值响应 */
export interface ActivityRechargeResponse {
    /** 请求是否成功 */
    status: boolean;
    /** 错误码，0 表示成功 */
    error_code: number;
    /** 错误消息 */
    message?: string;
    /** 响应数据 */
    data: {
        recharge_record_id?: string;
        error_id?: string;
        error_type?: string;
        detail?: any;
        context?: any;
    } | null;
}

// ==================== 档位配置 ====================

/** 档位配置 */
export interface TierConfig {
    tier: TierLevel;
    name: string;
    price: number;
    currency: string;
    creditsPerPeriod: number;
    features: string[];
    /** Stripe Price ID - 需要从 BO 获取或配置 */
    stripePriceId?: string;
}

/** 档位配置映射 */
export const TIER_CONFIGS: Record<TierLevel, TierConfig> = {
    [TierLevel.FREE]: {
        tier: TierLevel.FREE,
        name: 'Basic',
        price: 0,
        currency: 'USD',
        creditsPerPeriod: 5000,
        features: [
            '500MB Personal Vault storage',
            'Online view only; no downloads',
            'Access to basic research Agents'
        ],
    },
    [TierLevel.PRO_100]: {
        tier: TierLevel.PRO_100,
        name: 'Pro',
        price: 149,
        currency: 'USD',
        creditsPerPeriod: 50000,
        features: [
            '51GB Personal Vault storage',
            'Enable report exports and data downloads',
            'Unlock Advanced Domain Agents'
        ],
    },
    [TierLevel.PRO_200]: {
        tier: TierLevel.PRO_200,
        name: 'Pro',
        price: 200,
        currency: 'USD',
        creditsPerPeriod: 120000,
        features: [
            '51GB Personal Vault storage',
            'Enable report exports and data downloads',
            'Unlock Advanced Domain Agents'
        ],
    },
    [TierLevel.PRO_400]: {
        tier: TierLevel.PRO_400,
        name: 'Pro',
        price: 400,
        currency: 'USD',
        creditsPerPeriod: 280000,
        features: [
            '51GB Personal Vault storage',
            'Enable report exports and data downloads',
            'Unlock Advanced Domain Agents'
        ],
    },
    [TierLevel.PRO_600]: {
        tier: TierLevel.PRO_600,
        name: 'Pro',
        price: 600,
        currency: 'USD',
        creditsPerPeriod: 500000,
        features: [
            '51GB Personal Vault storage',
            'Enable report exports and data downloads',
            'Unlock Advanced Domain Agents'
        ],
    },
};
